'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { AlumniaLogo } from '@/components/alumnia-logo'
import { Button } from '@/components/ui/button'

/**
 * Custom 404 page — renders when no route matches.
 *
 * Brand framing: Alumnia is a map-based alumni network, so the "not found"
 * state leans into the directory map metaphor — a pin that has drifted off
 * the map. The literal "Page not found" headline does the explaining; the
 * illustration carries the brand. Visually a matched family with `error.tsx`.
 */
export default function NotFound() {
  const t = useTranslations('errors')
  const router = useRouter()

  // Only offer "Go back" when it stays inside the app. A 404 reached cold
  // (stale email link, Google result, typed URL) has no safe in-app history —
  // router.back() there either no-ops or bounces the user off-site.
  const [canGoBack, setCanGoBack] = useState(false)
  useEffect(() => {
    const sameOrigin =
      !document.referrer || new URL(document.referrer).origin === window.location.origin
    // Browser-only history/referrer can't be read during SSR, so this must
    // run post-mount — the canonical case for a one-time effect setState.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCanGoBack(window.history.length > 1 && sameOrigin)
  }, [])

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="mx-auto w-full max-w-md text-center">
        <Link
          href="/"
          className="mb-10 inline-block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <AlumniaLogo size={40} />
        </Link>

        {/* Off-the-map illustration: a faint map, a dashed route, a drifted pin */}
        <div className="mb-8 flex justify-center">
          <svg
            className="h-auto w-60 drop-shadow-sm"
            viewBox="0 0 240 150"
            fill="none"
            aria-hidden="true"
          >
            {/* Map card */}
            <rect
              x="6"
              y="14"
              width="228"
              height="122"
              rx="16"
              className="fill-blue-50 stroke-blue-100"
              strokeWidth="2"
            />
            {/* Soft contour lines — terrain, not a generic grid */}
            <g className="stroke-blue-100" strokeWidth="1.5" fill="none">
              <path d="M6 58 C 70 50, 140 70, 234 52" />
              <path d="M6 96 C 60 84, 120 104, 234 86" />
            </g>
            {/* Origin marker — where the trail started */}
            <circle cx="52" cy="104" r="5" className="fill-white stroke-blue-300" strokeWidth="2" />
            {/* Dashed route trailing off toward the drifted pin */}
            <path
              d="M52 104 C 92 96, 124 76, 170 60"
              className="stroke-blue-300"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="2 8"
            />
            {/* Ground shadow stays put while the pin settles */}
            <ellipse cx="182" cy="58" rx="9" ry="3" className="fill-blue-950/10" />
            {/* Drifted pin — drops and settles once on load */}
            <g className="nf-pin [animation:nf-drop_0.6s_cubic-bezier(0.22,1,0.36,1)_both]">
              <path
                d="M182 18c-9.4 0-17 7.4-17 16.6 0 11.9 15.2 22.6 16.2 23.3a1.4 1.4 0 0 0 1.6 0c1-.7 16.2-11.4 16.2-23.3C199 25.4 191.4 18 182 18Z"
                className="fill-blue-900"
              />
              <circle cx="182" cy="34.5" r="6" className="fill-white" />
            </g>
            <style>{`
              @keyframes nf-drop {
                0% { transform: translateY(-16px); opacity: 0; }
                60% { transform: translateY(2px); opacity: 1; }
                100% { transform: translateY(0); }
              }
              @media (prefers-reduced-motion: reduce) {
                .nf-pin {
                  animation: none !important;
                  transform: none !important;
                  opacity: 1 !important;
                }
              }
            `}</style>
          </svg>
        </div>

        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-500">404</p>
        <h1 className="mb-2 text-2xl font-semibold text-gray-900">{t('pageNotFound')}</h1>
        <p className="mb-7 text-sm text-gray-600">{t('notFoundDescription')}</p>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button asChild size="lg">
            <Link href="/">{t('goHome')}</Link>
          </Button>
          {canGoBack && (
            <Button variant="outline" size="lg" onClick={() => router.back()}>
              {t('goBack')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
